import { ArrowRight, ExternalLink, LogOut, PenLine, Search, ShieldCheck, Smartphone } from "lucide-react";
import { InfoHint } from "@/frontend/components/ui/info-hint";
import { logoUrl } from "@/frontend/utils/shared";
import { patientGreetingName, patientNameInSentence } from "@/frontend/utils/patient-name";

/**
 * A preview of the screen this patient actually lands on, framed as a phone. It
 * mirrors the portal body of `AuthenticatedView` and is non-interactive, so
 * nothing here can start a real OAuth flow. The frame is a literal 280x611px
 * rather than a flex ratio, whose width grew until the device overflowed.
 */
export function PatientLinkPreview({
  patientFirstName,
  patientFullName,
  askConsentSignature,
  showMobileApp,
  suggestedSources,
}: {
  patientFirstName: string | null;
  /** Full name, so the greeting matches the real page's shortening rule. */
  patientFullName?: string | null;
  askConsentSignature: boolean;
  showMobileApp: boolean;
  suggestedSources: { name: string; logo: string | null }[];
}) {
  const patient = patientNameInSentence(patientFirstName);
  const greetingName = patientGreetingName(patientFullName ?? patientFirstName);

  return (
    // The caller paints the muted ground; this is the stack that sits on it, and
    // it scrolls when the window is shorter than the device.
    <div className="flex min-h-0 flex-1 flex-col gap-3 p-4 lg:overflow-y-auto">
      <div className="flex shrink-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-meta font-semibold uppercase tracking-wide text-foreground">
        <Smartphone className="size-3.5" />
        What <span data-patient>{patient}</span> will see
        <InfoHint label="A live preview. It updates as you change the settings on the left." />
      </div>

      <p className="flex shrink-0 items-start gap-1.5 rounded-lg border bg-background px-2.5 py-2 text-caption text-muted-foreground">
        <ShieldCheck className="mt-0.5 size-3.5 shrink-0" />
        <span>
          Before this screen, <span data-patient>{patient}</span> reviews how records are shared and
          agrees to the terms.
        </span>
      </p>

      {/* Phone shell: thick bezel, hardware buttons, notch. One fixed 280x611px
          frame, never sized from the column or from what is inside it. */}
      <div className="relative mx-auto flex h-[38.1875rem] w-[17.5rem] shrink-0 flex-col">
        <span
          aria-hidden
          className="absolute -left-[3px] top-24 h-10 w-[3px] rounded-l bg-foreground/80"
        />
        <span
          aria-hidden
          className="absolute -right-[3px] top-20 h-14 w-[3px] rounded-r bg-foreground/80"
        />

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[2.25rem] border-[8px] border-foreground/90 bg-background shadow-lg">
          <div className="relative flex h-6 shrink-0 items-center justify-center bg-foreground/90">
            <span aria-hidden className="h-1.5 w-16 rounded-full bg-background/25" />
          </div>

          {/* The screen: fixed by the frame, scrolling whatever the settings put inside it. */}
          <div
            aria-label="Preview of the screen the patient will see"
            className="min-h-0 flex-1 overflow-y-auto px-4 py-4"
          >
            <div className="flex items-start justify-between gap-2">
              <h3 className="min-w-0 break-words text-base font-bold tracking-tight">
                {greetingName ? (
                  <>
                    Welcome, <span data-patient>{greetingName}</span>
                  </>
                ) : (
                  "Hi there"
                )}
              </h3>
              <span className="flex shrink-0 items-center gap-1 text-caption text-muted-foreground">
                <LogOut className="size-3" />
                Sign out
              </span>
            </div>

            <p className="mt-3 text-caption leading-snug text-muted-foreground">
              Link your patient portals to securely share your medical records, lab results, and
              visit history. Just tap &quot;Connect&quot; and sign in with your existing portal
              credentials.
            </p>

            {askConsentSignature && (
              <>
                <h4 className="mt-4 text-caption font-semibold">Provide your signature</h4>
                <div className="mt-2 flex items-center gap-2 rounded-lg bg-muted/50 p-2">
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted">
                    <PenLine className="size-3.5 text-foreground/60" />
                  </span>
                  <span className="min-w-0 flex-1 text-caption font-medium">
                    Authorization signature
                  </span>
                  <span className="shrink-0 rounded-md bg-foreground px-2 py-1 text-meta font-medium text-background">
                    Sign
                  </span>
                </div>
              </>
            )}

            <h4 className="mt-4 text-caption font-semibold">Link your patient portals</h4>

            {suggestedSources.length === 0 ? (
              <p className="mt-2 text-caption text-muted-foreground">
                No patient portals have been recommended yet.
              </p>
            ) : (
              <div className="mt-2 flex flex-col gap-2">
                {suggestedSources.map((source, index) => {
                  const url = logoUrl(source.logo);
                  return (
                    <div
                      key={`${source.name}-${index}`}
                      className="flex items-center gap-2 rounded-lg bg-muted/50 p-2"
                    >
                      <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-background">
                        {url ? (
                          <img
                            src={url}
                            alt=""
                            className="size-5 object-contain"
                            data-ph-no-capture
                            onError={(event) => {
                              event.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <span className="text-meta font-medium text-muted-foreground">
                            {source.name.slice(0, 2).toUpperCase()}
                          </span>
                        )}
                      </span>
                      {/* Wraps in full, never an ellipsis: systems sharing a long
                          prefix ellipsize to the same string and stop being
                          distinguishable. */}
                      <span
                        className="min-w-0 flex-1 break-words text-caption font-medium leading-snug"
                        data-patient
                      >
                        {source.name}
                      </span>
                      <span className="flex shrink-0 items-center gap-1 rounded-md bg-foreground px-2 py-1 text-meta font-medium text-background">
                        Connect
                        <ExternalLink className="size-2.5" />
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-2 flex items-center gap-2 rounded-lg border px-2.5 py-2">
              <Search className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate text-caption text-muted-foreground">
                Search for other healthcare organizations…
              </span>
            </div>

            {showMobileApp && (
              <>
                <div className="mt-4 border-t" />
                {/* Mirrors MobileAppSection, the block the patient actually gets:
                    a heading, the two stores, then the hand-off into the app. */}
                <h4 className="mt-3 text-caption font-semibold">Sync your health apps</h4>
                <p className="mt-1 text-meta leading-snug text-muted-foreground">
                  Download our app to automatically sync steps, heart rate, sleep, and other health
                  data from Apple Health or Google Fit.
                </p>

                <div className="mt-3 flex items-center gap-1.5">
                  <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-foreground text-[8px] font-medium text-background">
                    1
                  </span>
                  <span className="text-meta font-medium">Download the app</span>
                </div>
                <div className="mt-2 flex gap-2">
                  <span className="flex h-8 flex-1 items-center justify-center rounded-md bg-foreground text-meta font-medium text-background">
                    App Store
                  </span>
                  <span className="flex h-8 flex-1 items-center justify-center rounded-md bg-foreground text-meta font-medium text-background">
                    Google Play
                  </span>
                </div>

                <div className="mt-3 flex items-center gap-1.5">
                  <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-foreground text-[8px] font-medium text-background">
                    2
                  </span>
                  <span className="text-meta font-medium">Connect the app</span>
                </div>
                <span className="mt-2 flex h-8 w-fit items-center gap-1.5 rounded-md border px-2.5 text-meta font-medium">
                  <ArrowRight className="size-3" />
                  Continue in app
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
